import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { IconSettings } from '@tabler/icons-react'

import { fetchingAPI, apiService } from '@/api'
import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import {
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  fetchManagerAlertCoverage,
} from '@/reducers/notifications/notifications.actions'
import { TOGGLE_PANEL } from '@/reducers/notifications/notifications.redux'

import { NotificationItem } from './NotificationItem'
import { NotificationPreferences } from './NotificationPreferences'
import { LIVE_COACHING_TYPES, liveAlertForNotification } from './notificationActions'
import { ALL_NOTIFICATION_TYPES } from './notificationTypes.constants'

// The panel closes on mousedown, but the browser still dispatches the `click`
// of that same press afterwards — and on the drawer's backdrop, `click` is
// what closes the drawer. Swallow exactly that one click in the capture phase
// (before React's document-level listener sees it). The target re-check keeps
// a press-on-backdrop-release-elsewhere from eating an unrelated click.
const swallowNextDrawerOverlayClick = () => {
  const swallowClick = (clickEvent) => {
    if (clickEvent.target.closest('.drawer-overlay')) {
      clickEvent.stopPropagation()
      clickEvent.preventDefault()
    }
  }
  // A press whose click never arrives (released outside the window) would
  // leave the swallower armed to eat a later legitimate backdrop click; the
  // next press always precedes the next click, so it disarms the stale one.
  const disarmStaleSwallow = () => {
    document.removeEventListener('click', swallowClick, { capture: true })
  }
  document.addEventListener('click', swallowClick, { capture: true, once: true })
  // Capture phase so a widget stopping mousedown propagation can't skip the disarm.
  document.addEventListener('mousedown', disarmStaleSwallow, { capture: true, once: true })
}

export const NotificationPanel = ({ collapsed = false }) => {
  const dispatch = useDispatch()
  const panelRef = useRef(null)
  const [showPreferences, setShowPreferences] = useState(false)
  // null = not yet loaded; number = count fetched. Drives whether to show
  // the "Turn on alerts" CTA in the empty state.
  const [subscriptionsCount, setSubscriptionsCount] = useState(null)
  const [creatingDefault, setCreatingDefault] = useState(false)
  // The audience scope (all_users / user_ids / tag_ids) from the user's
  // existing settings row, or null if they have none yet. Lets the
  // "Turn on alerts" CTA re-enable events without clobbering a previously
  // chosen tag/user scope (subscriptionsCount === 0 means "matrix all-off",
  // NOT "no row exists").
  const [existingScope, setExistingScope] = useState(null)
  // Default to unread-only — the bell is a "what needs my attention right now"
  // surface, not an archive.
  const [showUnreadOnly, setShowUnreadOnly] = useState(true)
  // Snapshot of notification IDs that were unread *when the panel opened*.
  // The "Unread only" filter keeps these visible for the rest of the open
  // session, even after they get marked read by a body click. Without this,
  // clicking a row would instantly evict it, leaving no room for follow-up
  // (e.g. clicking a second action button). Reset to a fresh snapshot
  // every time the panel re-opens.
  // Array, not a Set — the FE style guide forbids Set/Map in state.
  // Membership check via .includes() is fine at panel-size N.
  const [stickyUnreadIds, setStickyUnreadIds] = useState([])
  const { notifications, loading, panelOpen } = useSelector((state) => state.notifications)
  // Live RTC alerts keyed by agent username. A live-coaching row (raised_hand /
  // RTA) whose agent is still on the SAME call it fired for stays visible in the
  // bell even after it's been marked read (using its Listen/Chat marks it read),
  // so the unread-only filter doesn't hide it on reopen while the call is still
  // on and the manager loses the buttons. The match is by callId
  // (isLiveCoachingRow, below); a row for an older call isn't kept sticky. Cold
  // rows (no in-memory alert this session) aren't covered here — they resolve
  // their join context at click time in NotificationItem.
  const liveAlerts = useSelector((state) => state.realtimeUserAlerts?.alerts)
  const managerId = useSelector((state) => state.currentUser?.user_id)

  // v1 collapsed the old multi-subscription model into a single
  // /settings row per user. The "subscriptionsCount" the rest of this
  // component watches is now 1-if-any-event-is-on / 0-if-all-off, so the
  // onboarding empty state ("Turn on alerts") triggers when every cell
  // in events_matrix is false.
  const fetchSubscriptionsCount = async () => {
    try {
      const response = await fetchingAPI(
        `${apiService.notification}/notifications/settings`,
        'GET',
        dispatch
      )
      const settings = response?.settings
      const matrix = settings?.events_matrix || {}
      const hasAnyEnabled = Object.values(matrix).some((channels) =>
        Object.values(channels || {}).some(Boolean)
      )
      setSubscriptionsCount(hasAnyEnabled ? 1 : 0)
      setExistingScope(
        settings
          ? {
              all_users: Boolean(settings.all_users),
              user_ids: settings.user_ids || [],
              tag_ids: settings.tag_ids || [],
            }
          : null
      )
    } catch (error) {
      setSubscriptionsCount(null)
    }
  }

  useEffect(() => {
    if (panelOpen) {
      dispatch(fetchNotifications({ limit: 20 }))
      // Only refetch subscriptionsCount on first open per session.
      // Its only consumer is the "Turn on alerts" CTA in the empty
      // state — that doesn't change between panel toggles unless the
      // user explicitly clicks the CTA (which refetches inline). Re-
      // fetching on every open burned a RTT per click for no payoff.
      if (subscriptionsCount === null) {
        fetchSubscriptionsCount()
      }
      // Refresh on every open — drives the raised_hand "set up an alert for
      // this agent" guidance. Unlike subscriptionsCount, coverage DOES change
      // mid-session (the manager can add/edit an alert config), and a stale
      // once-per-session cache would wrongly show "set up alerts" for an agent
      // they actually cover. It's a small payload, so refetch each open.
      dispatch(fetchManagerAlertCoverage(managerId))
    } else {
      // Panel closed → next open should start with a fresh sticky set.
      setStickyUnreadIds([])
    }
  }, [panelOpen])

  // Take the snapshot the first time we see notifications after open.
  // We do it lazily (here, not in the open useEffect) because the panel
  // opens before fetchNotifications resolves; if we snapshotted at open
  // we'd capture an empty set.
  useEffect(() => {
    if (!panelOpen || !isEmpty(stickyUnreadIds)) {
      return
    }
    const initialUnread = notifications
      .filter((notification) => !notification.read)
      .map((notification) => notification.id)
    if (!isEmpty(initialUnread)) {
      setStickyUnreadIds(initialUnread)
    }
  }, [panelOpen, notifications, stickyUnreadIds.length])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        const bellButton = document.querySelector('[data-testid="notification-bell"]')
        if (bellButton && bellButton.contains(event.target)) {
          return
        }
        // Don't close when interacting with portaled overlays (modals, toasts,
        // dropdowns) — they live outside panelRef but are part of the panel UX.
        // The drawer aside (role="dialog" with .drawer) is NOT in this group:
        // the panel (z-index 100000) always sits above drawers, so a click on
        // a drawer or its backdrop is an outside click that dismisses the panel.
        if (event.target.closest('[role="dialog"]:not(.drawer), [data-sonner-toast]')) {
          return
        }
        // The drawer's full-viewport backdrop closes the drawer on click. When
        // the panel is open on top, that same physical click must dismiss ONLY
        // the panel (QCER-901) — swallow the click before it reaches the
        // backdrop's onClick, so the drawer survives and a second click closes
        // it. This branch closes the panel via the shared dispatch below.
        // Primary button only: right/middle presses close the panel too (any-
        // button mousedown, as everywhere else) but never produce a `click`,
        // so arming on them would leave a stale swallower.
        if (event.button === 0 && event.target.closest('.drawer-overlay')) {
          swallowNextDrawerOverlayClick()
        }
        dispatch({ type: TOGGLE_PANEL })
      }
    }

    if (panelOpen && !showPreferences) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [panelOpen, showPreferences])

  useEffect(() => {
    if (!panelOpen || showPreferences) {
      return undefined
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        // The panel is the topmost layer, so Escape dismisses it first.
        // preventDefault is the codebase's "an inner layer handled Escape"
        // signal — useEscapeKey consumers (the Drawer) skip defaultPrevented
        // events, so the drawer stays open for the next Escape. Capture phase
        // guarantees we run before those bubble-phase listeners.
        event.preventDefault()
        dispatch({ type: TOGGLE_PANEL })
      }
    }
    document.addEventListener('keydown', handleEscape, { capture: true })
    return () => document.removeEventListener('keydown', handleEscape, { capture: true })
  }, [panelOpen, showPreferences])

  const handleClickNotification = (notification) => {
    // Body click only marks the row read — no navigation, no panel
    // close. Navigation is reserved for the per-type action buttons
    // (Start listening, Chats, Coaching Task ↗, etc.) so the body remains
    // a low-stakes "I saw this" target.
    if (!notification.read) {
      dispatch(markAsRead(notification.id))
    }
  }

  const handleMarkAllRead = () => {
    dispatch(markAllAsRead())
    // Also drop the rows from the list. The sticky-unread snapshot normally
    // keeps a row visible after it's marked read (so a body click doesn't
    // instantly evict it mid-interaction) — but "Mark all as read" is an
    // explicit "clear these out" gesture, so we retire the snapshot and let
    // the Unread-only filter empty the panel.
    setStickyUnreadIds([])
  }

  const handleOpenPreferences = () => {
    setShowPreferences(true)
  }

  const handleClosePreferences = () => {
    setShowPreferences(false)
  }

  const handleSavedPreferences = () => {
    setShowPreferences(false)
    // Settings just changed — the onboarding CTA keys off subscriptionsCount,
    // so refetch it instead of leaving a stale "no alerts" empty state up
    // until a full page reload.
    fetchSubscriptionsCount()
  }

  // One-click "Turn on alerts" — flips every known event type's in_app
  // channel on and re-enables the row. Preserves the user's existing
  // audience scope (all_users / tags / specific users) when they already
  // have a row, so re-enabling events doesn't silently widen a tag/user
  // scope to all-agents-in-org. Defaults to all_users only for genuine
  // first-time setup (no prior row). The user can refine per-event /
  // per-channel and re-scope via the gear later.
  const handleTurnOnAlerts = async () => {
    try {
      setCreatingDefault(true)
      const events_matrix = Object.fromEntries(
        ALL_NOTIFICATION_TYPES.map((type) => [type.key, { in_app: true }])
      )
      const hasPriorScope =
        existingScope &&
        (existingScope.all_users ||
          !isEmpty(existingScope.user_ids) ||
          !isEmpty(existingScope.tag_ids))
      const scope = hasPriorScope ? existingScope : { all_users: true, user_ids: [], tag_ids: [] }
      await fetchingAPI(
        `${apiService.notification}/notifications/settings`,
        'PUT',
        dispatch,
        JSON.stringify({
          ...scope,
          events_matrix,
          enabled: true,
        })
      )
      toast.success('Alerts turned on')
      await fetchSubscriptionsCount()
    } catch (error) {
      toast.error('Failed to turn on alerts', { cause: error })
    } finally {
      setCreatingDefault(false)
    }
  }

  if (!panelOpen && !showPreferences) {
    return null
  }

  const showOnboarding = subscriptionsCount === 0 && isEmpty(notifications) && !loading
  // A live-coaching row is still actionable while the agent is on the SAME call
  // this row fired for, so keep it visible under the unread filter even once
  // read. Match on callId (via liveAlertForNotification) rather than "any live
  // alert for this agent", so a row for an older call doesn't stay sticky once
  // the agent has moved on to a different one. Cold rows (no in-memory alert
  // yet) aren't covered here — the resolve-at-click path in NotificationItem
  // handles those.
  const isLiveCoachingRow = (notification) =>
    LIVE_COACHING_TYPES.includes(notification.notificationType) &&
    notification.refTable === 'agent_username' &&
    Boolean(
      // requireCallId: a row with no call id at all (pre-persistence rows,
      // stripped metadata) must NOT match "any live alert for this agent" —
      // that made every old RTA row resurface whenever the agent raised a
      // hand and vanish when they lowered it.
      liveAlertForNotification(liveAlerts?.[notification.refId], notification, {
        requireCallId: true,
      })
    )
  const visibleNotifications = showUnreadOnly
    ? notifications.filter(
        (notification) =>
          !notification.read ||
          stickyUnreadIds.includes(notification.id) ||
          isLiveCoachingRow(notification)
      )
    : notifications

  // Portal directly to document.body to bypass ancestor containing blocks
  // (the sidebar uses overflow:hidden and may have transform/filter on its
  // tree, which would otherwise re-anchor position:fixed to that ancestor).
  return ReactDOM.createPortal(
    <>
      {panelOpen && !showPreferences && (
        <div
          className={classNames('notification-panel', { collapsed })}
          ref={panelRef}
          data-testid="notification-panel"
        >
          <div className="notification-panel__header">
            <span className="notification-panel__title">Notifications</span>
            <div className="flex-align-center small-gap">
              <Button
                basic
                square
                small
                icon={<IconSettings />}
                tooltip="Settings"
                onClick={handleOpenPreferences}
                aria-label="Settings"
                data-testid="notification-preferences-button"
              />
              {!showOnboarding && (
                <Button size="small" onClick={handleMarkAllRead} data-testid="mark-all-read-button">
                  Mark all as read
                </Button>
              )}
            </div>
          </div>
          {!showOnboarding && (
            <div
              className="notification-panel__filter"
              role="tablist"
              aria-label="Notification filter"
            >
              <button
                type="button"
                role="tab"
                className={classNames('notification-panel__filter-button', {
                  'notification-panel__filter-button--active': showUnreadOnly,
                })}
                onClick={() => setShowUnreadOnly(true)}
                data-testid="filter-show-unread"
              >
                Unread only
              </button>
              <button
                type="button"
                role="tab"
                className={classNames('notification-panel__filter-button', {
                  'notification-panel__filter-button--active': !showUnreadOnly,
                })}
                onClick={() => setShowUnreadOnly(false)}
                data-testid="filter-show-all"
              >
                All notifications
              </button>
            </div>
          )}
          <div className="notification-panel__list">
            {loading && isEmpty(notifications) && (
              <div className="notification-panel__empty">Loading...</div>
            )}
            {showOnboarding && (
              <div
                className="notification-panel__onboarding"
                data-testid="notification-panel-onboarding"
              >
                <p className="notification-panel__onboarding-text">
                  You aren&apos;t set up to receive alerts yet.
                </p>
                <div className="notification-panel__onboarding-actions">
                  <Button
                    primary
                    onClick={handleTurnOnAlerts}
                    disabled={creatingDefault}
                    data-testid="turn-on-alerts-button"
                  >
                    {creatingDefault ? 'Turning on…' : 'Turn on alerts'}
                  </Button>
                  <Button onClick={handleOpenPreferences} data-testid="customize-alerts-link">
                    Customize…
                  </Button>
                </div>
              </div>
            )}
            {!loading && !showOnboarding && isEmpty(visibleNotifications) && (
              <div className="notification-panel__empty">
                {showUnreadOnly && !isEmpty(notifications)
                  ? 'No unread notifications'
                  : 'No notifications'}
              </div>
            )}
            {visibleNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClickNotification={handleClickNotification}
              />
            ))}
          </div>
        </div>
      )}
      <NotificationPreferences
        show={showPreferences}
        onClose={handleClosePreferences}
        onSaved={handleSavedPreferences}
      />
    </>,
    document.body
  )
}
